import { gql, useClient } from 'urql';

import { TransactionSearchOrder } from '@/globalTypes';
import { useQueryParams } from '@/hooks';
import { downloadFile, mapVariables } from '@/utils';
import {
  PlayerGameRoundsReportQuery,
  PlayerGameRoundsReportQueryVariables,
} from './__generated__/usePlayerGameRoundsReport';
import { queryParams } from './queryParams';

const PLAYER_GAME_ROUNDS_REPORT_QUERY = gql`
  query PlayerGameRoundsReport(
    $id: String
    $rawId: String
    $playerId: ID
    $brand: BrandEnum
    $gameRoundId: String
    $minAmount: PositiveBigDecimal
    $maxAmount: PositiveBigDecimal
    $from: OffsetDateTime
    $to: OffsetDateTime
    $currency: String
    $paymentStatus: [PaymentStatus!]
    $paymentMethod: String
    $adjustmentType: AdjustmentType
    $adjustmentCategory: AdjustmentCategory
    $orderBy: TransactionSearchOrder
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      exportedTransactionsV2(
        id: $id
        rawId: $rawId
        playerId: $playerId
        brand: $brand
        gameRoundId: $gameRoundId
        minAmount: $minAmount
        maxAmount: $maxAmount
        from: $from
        to: $to
        currency: $currency
        paymentStatus: $paymentStatus
        transactionTypes: [CasinoBet]
        paymentMethod: $paymentMethod
        adjustmentType: $adjustmentType
        adjustmentCategory: $adjustmentCategory
        orderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        url
      }
    }
  }
`;

// The orders of game rounds query is not the same as for exportedTransactionsV2
const gameRoundOrderByToTransactionOrderBy = (orderBy?: string) => {
  if (!orderBy) {
    return undefined;
  }

  // get the matching value from transaction search order or go with undefined
  return Object.entries(TransactionSearchOrder).find(
    ([key]) => key === orderBy,
  )?.[1];
};

export default function usePlayerGameRoundsReport(playerId: string) {
  const [query] = useQueryParams(queryParams);
  const client = useClient();

  const generate = async () => {
    const variables: PlayerGameRoundsReportQueryVariables = {
      ...mapVariables(query),
      orderBy: gameRoundOrderByToTransactionOrderBy(query.orderBy),
      playerId,
    };

    return client
      .query<PlayerGameRoundsReportQuery, PlayerGameRoundsReportQueryVariables>(
        PLAYER_GAME_ROUNDS_REPORT_QUERY,
        variables,
      )
      .toPromise()
      .then(async (res) => {
        const url = res.data?.viewer.exportedTransactionsV2.url;

        if (url) {
          await downloadFile(url, {
            fileName: 'player-game-rounds-report.csv',
          });
        }
      });
  };

  return {
    generate,
  };
}
